import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import config from '../config';
import ApiKeyManagement from './ApiKeyManagement';
import NotificationScheduleManagement from './NotificationScheduleManagement';
import VendorManagement from './VendorManagement/VendorManagement';
import UserManagement from './UserManagement';

interface PurchaseOrder {
  _id: string;
  DELIV_COMPL: boolean;
  PO_NO: number;
  VENDOR_ID: number;
  VENDOR_NAME: string;
  EXP_DATE: number;
  ITEMS: Array<{
    ITEM_PO: string;
    PLANT: number;
    MATERIAL_ID: string;
    MATERIAL_DESC: string;
    QUANTITY_PO: string;
    ORDER_UNIT: string;
    QUANTITY_TOBE_DELIV: string;
    MATERIAL_ALIAS?: string;
    MAGRV?: string;
    BEDAT?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

interface Stats {
  total: number;
  completed: number;
  pending: number;
  vendors: number;
}

const Dashboard: React.FC = () => {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, completed: 0, pending: 0, vendors: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPageGroup, setCurrentPageGroup] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [activeTab, setActiveTab] = useState<'dashboard' | 'api-keys' | 'notifications' | 'vendors' | 'users'>('dashboard');
  const [showPODetails, setShowPODetails] = useState(false);
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10'
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      if (statusFilter) {
        params.append('DELIV_COMPL', statusFilter);
      }
      
      const [ordersResponse, allOrdersResponse] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/po?${params.toString()}`),
        axios.get(`${config.API_BASE_URL}/po?limit=10000`) // For stats - increased limit
      ]);

      setPurchaseOrders(ordersResponse.data.data);
      setTotalPages(ordersResponse.data.pagination.totalPages);

      // Calculate stats
      const allOrders = allOrdersResponse.data.data;
      const total = allOrders.length;
      const completed = allOrders.filter((po: PurchaseOrder) => po.DELIV_COMPL).length;
      const pending = total - completed;
      const vendors = new Set(allOrders.map((po: PurchaseOrder) => po.VENDOR_ID)).size;

      setStats({ total, completed, pending, vendors });
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const mapExistingPurchaseOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await axios.post(
        `${config.API_BASE_URL}/po/map-existing-aliases`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setError('');
        // Reload data to show updated MATERIAL_ALIAS values
        await loadData();
        alert(`Successfully mapped ${response.data.data.mappedCount} purchase orders with vendor aliases.`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to map existing purchase orders');
      console.error('Error mapping purchase orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const remapPurchaseOrderAliases = async (poId: string) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Authentication token not found');
        return;
      }

      const response = await axios.post(
        `${config.API_BASE_URL}/po/${poId}/remap-aliases`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setError('');
        // Reload data to show updated MATERIAL_ALIAS values
        await loadData();
        // Close the modal and show success message
        setShowPODetails(false);
        alert('Successfully re-mapped vendor aliases for this purchase order.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to re-map purchase order aliases');
      console.error('Error re-mapping purchase order aliases:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (poNo: number, newStatus: boolean) => {
    try {
      await axios.put(`${config.API_BASE_URL}/po/status/${poNo}`, {
        DELIV_COMPL: newStatus
      });
      loadData(); // Reload data
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update status');
    }
  };

  const resetAllProcessed = async () => {
    if (!window.confirm('Are you sure you want to reset all orders to pending?')) {
      return;
    }

    try {
      await axios.put(`${config.API_BASE_URL}/po/admin/reset-processed`);
      loadData(); // Reload data
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset orders');
    }
  };



  const viewPODetails = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setShowPODetails(true);
  };

  const formatDate = (dateNumber: number) => {
    if (!dateNumber) return 'N/A';
    const dateStr = dateNumber.toString();
    if (dateStr.length === 8) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${day}/${month}/${year}`;
    }
    return dateNumber.toString();
  };

  useEffect(() => {
    loadData();
  }, [currentPage, searchTerm, statusFilter]);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1); // Reset to first page on search
    setCurrentPageGroup(1); // Reset to first page group on search
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setCurrentPage(1);
    setCurrentPageGroup(1);
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Pagination helper functions
  const pagesPerGroup = 10;
  const totalPageGroups = Math.ceil(totalPages / pagesPerGroup);
  const startPage = (currentPageGroup - 1) * pagesPerGroup + 1;
  const endPage = Math.min(currentPageGroup * pagesPerGroup, totalPages);
  const visiblePages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

  const goToNextPageGroup = () => {
    if (currentPageGroup < totalPageGroups) {
      const newGroup = currentPageGroup + 1;
      const newStartPage = (newGroup - 1) * pagesPerGroup + 1;
      setCurrentPageGroup(newGroup);
      setCurrentPage(newStartPage);
    }
  };

  const goToPreviousPageGroup = () => {
    if (currentPageGroup > 1) {
      const newGroup = currentPageGroup - 1;
      const newStartPage = (newGroup - 1) * pagesPerGroup + 1;
      setCurrentPageGroup(newGroup);
      setCurrentPage(newStartPage);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Update page group if necessary
    const pageGroup = Math.ceil(page / pagesPerGroup);
    if (pageGroup !== currentPageGroup) {
      setCurrentPageGroup(pageGroup);
    }
  };

  const canAccessTab = (tabName: string) => {
    if (!user?.role) return false;
    
    switch (tabName) {
      case 'dashboard':
        return true; // All roles can access dashboard
      case 'api-keys':
        return ['admin', 'supervisor', 'tech'].includes(user.role);
      case 'notifications':
        return ['admin', 'supervisor', 'tech'].includes(user.role);
      case 'vendors':
        return ['admin', 'supervisor', 'manager'].includes(user.role);
      case 'users':
        return user.role === 'admin'; // Only admins can manage users
      default:
        return false;
    }
  };

  // Redirect to dashboard if user doesn't have access to current tab
  useEffect(() => {
    if (activeTab !== 'dashboard' && !canAccessTab(activeTab)) {
      setActiveTab('dashboard');
    }
  }, [user?.role, activeTab]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100 bg-light">
      {/* Navigation */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark">
        <div className="container-fluid">
          <span className="navbar-brand">
            <i className="bi bi-database-gear me-2"></i>
            Client Data Manager
          </span>
          <div className="navbar-nav ms-auto">
            <div className="nav-item dropdown">
              <button
                className="btn btn-outline-light dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                <i className="bi bi-person-circle me-1"></i>
                {user?.username}
              </button>
              <ul className="dropdown-menu">
                <li>
                  <button className="dropdown-item" onClick={handleLogout}>
                    <i className="bi bi-box-arrow-right me-1"></i>
                    Logout
                  </button>
                </li>
              </ul>
            </div>
            {/* Direct logout button (always visible as backup) */}
            <button 
              className="btn btn-outline-danger ms-2"
              onClick={handleLogout}
              title="Logout"
            >
              <i className="bi bi-box-arrow-right me-1"></i>
              <span className="d-none d-md-inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <div className="container-fluid py-4">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError('')}
            ></button>
          </div>
        )}

        {/* Stats Cards */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card bg-primary text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h5>Total Orders</h5>
                    <h3>{stats.total}</h3>
                  </div>
                  <div className="align-self-center">
                    <i className="bi bi-basket3 fs-1"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-success text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h5>Completed</h5>
                    <h3>{stats.completed}</h3>
                  </div>
                  <div className="align-self-center">
                    <i className="bi bi-check-circle fs-1"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-warning text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h5>Pending</h5>
                    <h3>{stats.pending}</h3>
                  </div>
                  <div className="align-self-center">
                    <i className="bi bi-clock fs-1"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card bg-info text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between">
                  <div>
                    <h5>Vendors</h5>
                    <h3>{stats.vendors}</h3>
                  </div>
                  <div className="align-self-center">
                    <i className="bi bi-building fs-1"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="row align-items-center">
              <div className="col-md-8">
                <div className="btn-group" role="group">
                  <button
                    className={`btn ${activeTab === 'dashboard' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setActiveTab('dashboard')}
                  >
                    <i className="bi bi-speedometer2 me-1"></i>
                    Dashboard
                  </button>
                  {canAccessTab('api-keys') && (
                    <button
                      className={`btn ${activeTab === 'api-keys' ? 'btn-info' : 'btn-outline-info'}`}
                      onClick={() => setActiveTab('api-keys')}
                    >
                      <i className="bi bi-key me-1"></i>
                      API Keys
                    </button>
                  )}
                  {canAccessTab('notifications') && (
                    <button
                      className={`btn ${activeTab === 'notifications' ? 'btn-warning' : 'btn-outline-warning'}`}
                      onClick={() => setActiveTab('notifications')}
                    >
                      <i className="bi bi-bell me-1"></i>
                      Notifications
                    </button>
                  )}
                  {canAccessTab('vendors') && (
                    <button
                      className={`btn ${activeTab === 'vendors' ? 'btn-success' : 'btn-outline-success'}`}
                      onClick={() => setActiveTab('vendors')}
                    >
                      <i className="bi bi-building me-1"></i>
                      Vendors
                    </button>
                  )}
                  {canAccessTab('users') && (
                    <button
                      className={`btn ${activeTab === 'users' ? 'btn-secondary' : 'btn-outline-secondary'}`}
                      onClick={() => setActiveTab('users')}
                    >
                      <i className="bi bi-people me-1"></i>
                      Users
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-4 text-end">
                {/* Admin actions moved to Users tab */}
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard-specific Search and Filter Controls */}
        {activeTab === 'dashboard' && (
          <div className="card mb-4">
            <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label htmlFor="search" className="form-label">
                  <i className="bi bi-search me-1"></i>
                  Search Purchase Orders
                </label>
                <div className="input-group">
                  <input
                    type="text"
                    className="form-control"
                    id="search"
                    placeholder="Search by PO number or vendor name..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                  />
                  <button 
                    className="btn btn-outline-primary" 
                    type="button"
                    onClick={handleSearch}
                    title="Search"
                  >
                    <i className="bi bi-search"></i>
                  </button>
                  {(searchInput || searchTerm) && (
                    <button 
                      className="btn btn-outline-secondary" 
                      type="button"
                      onClick={handleClearSearch}
                      title="Clear search"
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  )}
                </div>
              </div>
              <div className="col-md-3">
                <label htmlFor="statusFilter" className="form-label">
                  <i className="bi bi-funnel me-1"></i>
                  Delivery Status
                </label>
                <select
                  className="form-select"
                  id="statusFilter"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1); // Reset to first page on filter
                    setCurrentPageGroup(1); // Reset to first page group on filter
                  }}
                >
                  <option value="">All Status</option>
                  <option value="true">Completed</option>
                  <option value="false">Pending</option>
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Actions</label>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={mapExistingPurchaseOrders}
                  disabled={loading}
                  title="Map existing purchase orders with vendor aliases"
                >
                  <i className="bi bi-link me-1"></i>
                  Map Aliases
                </button>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Tab Content */}
        {activeTab === 'dashboard' && (
          <>
            {/* Dashboard Content - Stats and Purchase Orders */}
            <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col-md-6">
                <h5 className="mb-0">
                  <i className="bi bi-table me-2"></i>
                  Purchase Orders
                </h5>
              </div>
              <div className="col-md-6 text-end">
                <button
                  className="btn btn-warning me-2"
                  onClick={resetAllProcessed}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Reset All to Pending
                </button>
                <button
                  className="btn btn-primary"
                  onClick={loadData}
                >
                  <i className="bi bi-arrow-clockwise me-1"></i>
                  Refresh
                </button>
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>PO Number</th>
                    <th>Vendor</th>
                    <th>Vendor ID</th>
                    <th>Exp Date</th>
                    <th>Items</th>
                    <th>Date Added</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrders.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-4">
                        <i className="bi bi-inbox fs-1 text-muted"></i>
                        <p className="text-muted mt-2">No purchase orders found</p>
                      </td>
                    </tr>
                  ) : (
                    purchaseOrders.map((order) => (
                      <tr key={order._id} style={{ cursor: 'pointer' }} onClick={() => viewPODetails(order)}>
                        <td><strong>{order.PO_NO}</strong></td>
                        <td>{order.VENDOR_NAME}</td>
                        <td>{order.VENDOR_ID}</td>
                        <td>{formatDate(order.EXP_DATE)}</td>
                        <td>
                          <span className="badge bg-info">{order.ITEMS.length}</span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {new Date(order.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </small>
                        </td>
                        <td>
                          {order.DELIV_COMPL ? (
                            <span className="badge bg-success">Completed</span>
                          ) : (
                            <span className="badge bg-warning">Pending</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <nav>
                <ul className="pagination justify-content-center">
                  {/* Previous Page */}
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <i className="bi bi-chevron-left"></i>
                      Previous
                    </button>
                  </li>

                  {/* Previous Page Group */}
                  {currentPageGroup > 1 && (
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={goToPreviousPageGroup}
                        title={`Go to pages ${(currentPageGroup - 2) * pagesPerGroup + 1}-${(currentPageGroup - 1) * pagesPerGroup}`}
                      >
                        <i className="bi bi-chevron-double-left"></i>
                      </button>
                    </li>
                  )}

                  {/* Page Numbers */}
                  {visiblePages.map((page) => (
                    <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    </li>
                  ))}

                  {/* Next Page Group */}
                  {currentPageGroup < totalPageGroups && (
                    <li className="page-item">
                      <button
                        className="page-link"
                        onClick={goToNextPageGroup}
                        title={`Go to pages ${currentPageGroup * pagesPerGroup + 1}-${Math.min((currentPageGroup + 1) * pagesPerGroup, totalPages)}`}
                      >
                        <i className="bi bi-chevron-double-right"></i>
                      </button>
                    </li>
                  )}

                  {/* Next Page */}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <i className="bi bi-chevron-right"></i>
                    </button>
                  </li>
                </ul>

                {/* Pagination Info */}
                <div className="d-flex justify-content-between align-items-center mt-2">
                  <small className="text-muted">
                    Showing page {currentPage} of {totalPages}
                    {totalPageGroups > 1 && (
                      <span> (Group {currentPageGroup} of {totalPageGroups})</span>
                    )}
                  </small>
                  <small className="text-muted">
                    Pages {startPage}-{endPage} of {totalPages}
                  </small>
                </div>
              </nav>
            )}
          </div>
        </div>
          </>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && canAccessTab('api-keys') && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-key me-2"></i>
                  API Key Management
                </h5>
              </div>
              <div className="card-body">
                <ApiKeyManagement />
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && canAccessTab('notifications') && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-bell me-2"></i>
                  Notification Schedule Management
                </h5>
              </div>
              <div className="card-body">
                <NotificationScheduleManagement />
              </div>
            </div>
          </div>
        )}

        {/* Vendors Tab */}
        {activeTab === 'vendors' && canAccessTab('vendors') && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-building me-2"></i>
                  Vendor Management
                </h5>
              </div>
              <div className="card-body p-0">
                <VendorManagement />
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && canAccessTab('users') && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bi bi-people me-2"></i>
                  User Management
                </h5>
              </div>
              <div className="card-body p-0">
                <UserManagement />
              </div>
            </div>
          </div>
        )}



        {/* Purchase Order Details Modal */}
        {showPODetails && selectedPO && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-receipt me-2"></i>
                    Purchase Order Details - {selectedPO.PO_NO}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => setShowPODetails(false)}></button>
                </div>
                <PurchaseOrderDetails po={selectedPO} onRemapAliases={remapPurchaseOrderAliases} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};





// Purchase Order Details Component
interface PurchaseOrderDetailsProps {
  po: PurchaseOrder;
  onRemapAliases: (poId: string) => Promise<void>;
}

const PurchaseOrderDetails: React.FC<PurchaseOrderDetailsProps> = ({ po, onRemapAliases }) => {
  const formatDate = (dateNumber: number) => {
    if (!dateNumber) return 'N/A';
    const dateStr = dateNumber.toString();
    if (dateStr.length === 8) {
      const year = dateStr.substring(0, 4);
      const month = dateStr.substring(4, 6);
      const day = dateStr.substring(6, 8);
      return `${day}/${month}/${year}`;
    }
    return dateNumber.toString();
  };

  return (
    <div className="modal-body">
      {/* Header Information */}
      <div className="card mb-4">
        <div className="card-header">
          <h6 className="mb-0">
            <i className="bi bi-info-circle me-2"></i>
            Order Information
          </h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Purchase Order Number</label>
                <p className="form-control-plaintext">{po.PO_NO}</p>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">Vendor ID</label>
                <p className="form-control-plaintext">{po.VENDOR_ID}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Vendor Name</label>
                <p className="form-control-plaintext">{po.VENDOR_NAME}</p>
              </div>
              <div className="mb-3">
                <label className="form-label fw-bold">Expiry Date</label>
                <p className="form-control-plaintext">{formatDate(po.EXP_DATE)}</p>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Status</label>
                <p>
                  {po.DELIV_COMPL ? (
                    <span className="badge bg-success fs-6">Completed</span>
                  ) : (
                    <span className="badge bg-warning fs-6">Pending</span>
                  )}
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label fw-bold">Total Items</label>
                <p className="form-control-plaintext">{po.ITEMS.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Items Table */}
      <div className="card">
        <div className="card-header">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">
              <i className="bi bi-list-ul me-2"></i>
              Order Items
            </h6>
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              onClick={() => onRemapAliases(po._id)}
              title="Re-map vendor aliases for this purchase order"
            >
              <i className="bi bi-link me-1"></i>
              Re-map Aliases
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="table table-sm table-hover">
              <thead className="table-light">
                <tr>
                  <th>Item PO</th>
                  <th>Plant</th>
                  <th>Material ID</th>
                  <th>Description</th>
                  <th>Vendor Alias</th>
                  <th>Jenis Obat</th>
                  <th>PO Date</th>
                  <th>Quantity</th>
                  <th>Unit</th>
                  <th>To Deliver</th>
                </tr>
              </thead>
              <tbody>
                {po.ITEMS.map((item, index) => (
                  <tr key={index}>
                    <td><small>{item.ITEM_PO}</small></td>
                    <td><small>{item.PLANT}</small></td>
                    <td><small className="font-monospace">{item.MATERIAL_ID}</small></td>
                    <td><small>{item.MATERIAL_DESC}</small></td>
                    <td><small className="text-muted">{item.MATERIAL_ALIAS || 'No mapping'}</small></td>
                    <td><small>{item.MAGRV || '-'}</small></td>
                    <td><small>{item.BEDAT ? new Date(item.BEDAT).toLocaleDateString('en-GB') : '-'}</small></td>
                    <td><small>{item.QUANTITY_PO}</small></td>
                    <td><small>{item.ORDER_UNIT}</small></td>
                    <td><small>{item.QUANTITY_TOBE_DELIV}</small></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Metadata */}
      <div className="mt-3">
        <div className="row">
          <div className="col-md-6">
            <small className="text-muted">
              <strong>Created:</strong> {new Date(po.createdAt).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </small>
          </div>
          <div className="col-md-6">
            <small className="text-muted">
              <strong>Updated:</strong> {new Date(po.updatedAt).toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 